import queryString from "query-string";

import ReactBilling, { PaymentSystem } from '@web-solutions/react-billing';

import DeviceProps from '@magnus/react-native-device-props';

import { getQueryParameter } from "core/utils/query";

import API, { setBillingBaseUrlOverride, setCurrentPaymentSystem, getStripeBaseUrl } from './api';

import { getExtraParams } from './utils';

const Billing = {
  paymentSystemProject: '',
  paymentSystemType: '',
  stripeCustomerToken: '',

  setApiAuth(apiAuth) {
    this.apiAuth = apiAuth;
  },

  setAccessTokenUserApi(tokenUser) {
    this.accessTokenUser = tokenUser;
  },

  getPaymentParams({ paymentSystem, paymentProject } = {}) {
    return {
      payment_system_type: paymentSystem || this.paymentSystemType,
      payment_system_project: paymentProject || this.paymentSystemProject,
    };
  },

  getHeaders() {
    return {
      Authorization: this.apiAuth || 'Bearer 33529|9qxW7v8ymOa9gc2eTl5g2Erg2oazEYdjhajCguct',
    };
  },

  getHeadersUserAPI() {
    return {
      Authorization: `Bearer ${this.accessTokenUser}`,
    };
  },

  async init(paymentSystem, paymentProject) {
    console.log('[Billing] init called with:', { paymentSystem, paymentProject });
    console.log('[Billing] PaymentSystem.STRIPE:', PaymentSystem.STRIPE);
    console.log('[Billing] Is STRIPE?', paymentSystem === PaymentSystem.STRIPE);

    this.paymentSystemType = paymentSystem;
    this.paymentSystemProject = paymentProject;
    setCurrentPaymentSystem(paymentSystem);

    if (paymentSystem === PaymentSystem.STRIPE) {
      const stripeBillingUrl = getStripeBaseUrl();
      console.log('[Billing] STRIPE detected! URL:', stripeBillingUrl);
      console.log('[Billing] setting Stripe billing URL override:', stripeBillingUrl);
      setBillingBaseUrlOverride(stripeBillingUrl);
    } else if (
      paymentSystem === PaymentSystem.TRUEGATE &&
      process.env.REACT_APP_API_TRUEGATE_BILLING_URL
    ) {
      console.log('[Billing] setting Truegate billing URL override:', process.env.REACT_APP_API_TRUEGATE_BILLING_URL);
      setBillingBaseUrlOverride(process.env.REACT_APP_API_TRUEGATE_BILLING_URL);
    } else {
      console.log('[Billing] no URL override (paymentSystem=', paymentSystem, ', envSet=', !!process.env.REACT_APP_API_TRUEGATE_BILLING_URL, ')');
      setBillingBaseUrlOverride(null);
    }

    const headers = this.getHeaders();
    const params = paymentSystem === PaymentSystem.STRIPE ? {} : this.getPaymentParams();
    console.log('[Billing] Getting public key with params:', params);

    try {
      const keys = await API.getPublicKey(params, headers);
      console.log('[Billing] Got keys:', keys);
      return ReactBilling.init(paymentSystem, keys);
    } catch (error) {
      console.error('[Billing] Error getting public key:', error);
      throw error;
    }
  },

  async saveTokenRecurly(token) {
    const headersUserAPI = this.getHeadersUserAPI()

    return API.saveTokenRecurly({ token }, headersUserAPI)
  },

  async saveUserSessionLoopie(body) {
    const headersUserAPI = this.getHeadersUserAPI()

    return API.saveUserSessionLoopie(body, headersUserAPI)
  },

  async oneTimePurchaseRecurly(body) {
    const headersUserAPI = this.getHeadersUserAPI()

    return API.oneTimePurchaseRecurly(body, headersUserAPI)
  },

  async getPrices(pricesIds, { type = 'subscription', paymentSystem } = { type: 'subscription', paymentSystem: '' }) {
    const headers = this.getHeaders();
    const params = {
      type,
      ...(pricesIds?.length ? { search: `external_id:${pricesIds.join(',')}`, searchFields: 'external_id:in' } : {}),
      searchFields: 'external_id:in',
      ...this.getPaymentParams({ paymentSystem }),
    };
    return (await API.getPrices(params, headers)).data;
  },

  async createOrder({
    one_time_product,
    one_time_products,
    productId,
    orderDescription,
    products,
    currency,
    paymentSystem,
    trigger,
    metadata,
    solidMetadata,
    is_sending_terminate_links,
    is_sending_trial_end,
    paymentProject
  }) {
    const extra = await getExtraParams();

    const body = {
      ...this.getPaymentParams({ paymentSystem, paymentProject }),
      ...extra,
      one_time_product,
      trigger,
      metadata: JSON.stringify(metadata),
      solid_metadata: solidMetadata,
      one_time_products: one_time_products || undefined,
      product_id: productId,
      order_description: orderDescription,
      products,
      currency,
      is_sending_terminate_links,
      is_sending_trial_end,
    };

    return API.createOrder(body, this.getHeaders());
  },

  async initAlternativePayment({
    paymentMethod,
    paymentSystem,
    trigger,
    products,
    currency,
    metadata,
    one_time_products,
    orderDescription,
    is_sending_terminate_links,
    is_sending_trial_end,
    solid_metadata,
    first_name,
    last_name,
    taxpayer_id,
    success_url,
    fail_url,
    return_url,
  }) {
    const extra = await getExtraParams();

    const body = {
      ...this.getPaymentParams({ paymentSystem }),
      ...extra,
      products,
      solid_metadata,
      currency,
      metadata: JSON.stringify(metadata),
      one_time_products,
      trigger,
      order_description: orderDescription,
      is_sending_terminate_links,
      is_sending_trial_end,
      payment_method: paymentMethod,
      customer_first_name: first_name,
      customer_last_name: last_name,
      taxpayer_id,
      success_url,
      fail_url,
      return_url,
    };

    return API.initAlternativePayment(body, this.getHeaders())
  },

  async createCheckout({ productId, checkoutId, paymentMethod, successUrl, paymentSystem }) {
    const extra = await getExtraParams();
    const body = {
      ...this.getPaymentParams({ paymentSystem }),
      ...extra,
      product_id: productId,
      checkout_id: checkoutId,
      payment_method: paymentMethod,
      success_url: successUrl,
    };

    return API.createCheckout(body, this.getHeaders());
  },

  async createCustomer({ email, paymentSystem, paymentProject, defaultOnboardingType, learningGoal }) {
    // if (process.env.REACT_APP_APP_NAME === 'voery') {
      const headers = this.getHeaders();
      const body = {
        ...this.getPaymentParams(),
        ...(email && { email }),
      };

      return (await API.createCustomer(body, headers)).data;
    // }

  //   const onboardingType = getQueryParameter('variant') || defaultOnboardingType || 'default';
  //   const adName = getQueryParameter('ad_name') || 'organic';

  //   const socialMediaRaw = localStorage.getItem('socialMedia');
  //   const socialMedia = socialMediaRaw ? JSON.parse(socialMediaRaw) : null;

  //   const adMessage = getQueryParameter('ad_message') || '';

  //   const body = {
  //     ...this.getPaymentParams({ paymentSystem, paymentProject }),
  //     ...(email && { email }),
  //   };

  //   if (!body.payment_system_type || !body.payment_system_project) {
  //     return
  //   }

  //   const resBody = {
  //     email: body.email,
  //     paymentSystemType: body.payment_system_type,
  //     paymentSystemProject: body.payment_system_project,
  //     isWeb: true,
  //     deviceId: queryString.parse(window.location.search).idfm,
  //     onboardingType,
  //     language: "en",
  //     adName,
  //     adMessage,
  //     socialMediaType: socialMedia?.type ?? null,
  //     socialMediaLogin: socialMedia?.login ?? null,
  // }

  //   if (learningGoal) {
  //     resBody.learningGoal = learningGoal;
  //   }

  //   const { data } = (await API.registerUser(resBody, {}))

  //   this.setAccessTokenUserApi(data.token)

  //   return data;
  },

  async subscribe({ paymentSystem, metadata, ...formData }) {
    const headers = this.getHeaders();
    const extra = paymentSystem === PaymentSystem.STRIPE ? {} : await getExtraParams();
    const paymentParams = paymentSystem === PaymentSystem.STRIPE ? {} : this.getPaymentParams({ paymentSystem });

    const body = {
      ...extra,
      ...formData,
      ...paymentParams,
      ...(metadata && { metadata: JSON.stringify(metadata) }),
    };

    const response = await API.createSubscription(body, headers)
    // Stripe returns the payload directly (with subscriptionId), others wrap it in .data
    const data = response.data || response

    // Handle both transaction_id (for other systems) and subscriptionId (for Stripe)
    const transactionId = data.transaction_id || data.subscriptionId;
    if (transactionId) {
      localStorage.setItem('transactionId', transactionId);
    }

     const socialMediaRaw = localStorage.getItem('socialMedia');

     if (socialMediaRaw) {
      const headers = this.getHeadersUserAPI()
      await API.bootstrap(headers)
     }

    if (body?.token_id) {
      try {
        await this.saveTokenRecurly(body?.token_id)
      } catch (e) {
        console.error('save token recurly error', e)
      }
    }

    return {
      ...data,
      transaction_id: transactionId,
      paymentSystem: paymentSystem || data.paymentSystem
    };
  },

  async subscribeOnOneClick({ productId, metadata, ...params }) {
    const body = {
      ...this.getPaymentParams(),
      ...params,
      metadata: JSON.stringify(metadata),
      product_id: productId,
    };

    const response = await API.subscribeOnOneClick(body, this.getHeaders())

    return response
  },

  async applePayOrder({ paymentSystem, metadata, ...formData }) {
    const headers = this.getHeaders();
    const extra = await getExtraParams();

    const body = {
      ...extra,
      ...formData,
      customer_email: formData.email,
      product_id: formData.price_id,
      metadata: JSON.stringify(metadata),
      ...this.getPaymentParams({ paymentSystem }),
    };

    return API.createApplePayOrder(body, headers);
  },

  async getSubscriptions(isAsb) {
    const headers = this.getHeaders();
    if (isAsb) {
      return API.getAllSubscriptions(headers);
    }
    return API.getActualizedSubscriptions(headers);
  },

  async requestCancel({ email, trigger }) {
    const body = {
      email,
      trigger,
    };

    return API.cancelSubscription(body);
  },

  async submitCancelSubscription(params) {
    return API.submitCancelSubscription(params);
  },

  async createOneClickPayment({
    amount,
    currency,
    force3ds,
    productCode,
    productId,
    description,
    trigger,
    withTransactionDetailsEmail,
    transactionId
  }) {
    // Stripe has no /one-click endpoint — upsale purchases go through
    // POST /stripe/one-time-payment { productId, paymentMethodId }.
    // productId comes from GET /stripe/products?type=onetime (first product),
    // paymentMethodId is reused from the subscription card form.
    if (this.paymentSystemType === PaymentSystem.STRIPE) {
      const onetimeProducts = typeof window !== 'undefined' ? window.__stripe_onetime_products__ : null;
      const resolvedProductId = onetimeProducts?.[0]?.productId || productId;
      const paymentMethodId = typeof window !== 'undefined' ? window.__stripe_payment_method_id__ : undefined;
      console.log('[Billing] Stripe one-click -> createStripeOneTimePayment', { resolvedProductId, paymentMethodId });
      return this.createStripeOneTimePayment(resolvedProductId, paymentMethodId);
    }

    const headers = this.getHeaders();
    const { geo_country } = await getExtraParams();

    const { payment_system_project, payment_system_type } = this.getPaymentParams();

    const body = {
      payment_system_project,
      payment_system_type,
      geo_country,
      amount: Number(amount),
      withTransactionDetailsEmail,
      force3ds,
      description,
      currency,
      trigger,
      success_url: "https://...",
      fail_url: "https://...",
      product_code: productCode,
      product_id: payment_system_type === PaymentSystem.TRUEGATE ? productCode : productId,
      ...(payment_system_type === PaymentSystem.TRUEGATE && { transaction_id: transactionId }) // было: transactionId
    };

    return API.createOneClickPayment(body, headers);
  },

  async iqCertificateGenerate(body) {
    const headers = this.getHeadersUserAPI();

    try {
      return await API.iqCertificateGenerate(body, headers)
    } catch(err) {}
  },

  async getPaymentMethod() {
    const headers = this.getHeaders();
    const paymentMethod = await API.getPaymentMethod(headers);
    return paymentMethod?.data || paymentMethod;
  },

  async getOneTimePurchases() {
    const headers = this.getHeaders();
    const oneTimePurchases = await API.getOneTimePurchases(headers);
    return oneTimePurchases?.data || oneTimePurchases;
  },

  async sendAccessEmail(email) {
    const headers = this.getHeaders();
    const body = {
      email
    }

    return API.sendAccessEmail(body, headers);
  },

  async changePlan({ subscriptionId, productId, isNewSubscription, successUrl, failUrl }) {
    const headers = this.getHeaders();

    const body = {
      ...this.getPaymentParams(),
      subscriptionId,
      planCode: productId,
      isNewSubscription: !!isNewSubscription,
      success_url: successUrl,
      fail_url: failUrl,
    };

    return API.changePlan(body, headers);
  },

  async createOneClickFreePayment(productId, transactionId) {
    const headers = this.getHeadersUserAPI();
    const description = await DeviceProps.getIDFM();

    const body = {
      paymentSystemType: this.getPaymentParams().payment_system_type,
      paymentSystemProject: this.getPaymentParams().payment_system_project,
      productCode: productId,
      ...(transactionId && { transactionId }),
      description
    };

    return API.createFreeOneClickPayment(body, headers);
  },

  async getStripeProducts(type) {
    const headers = this.getHeaders();
    console.log('[Billing] getStripeProducts called with type:', type);
    try {
      const response = await API.getStripeProducts(type, headers);
      console.log('[Billing] getStripeProducts success:', response);
      return response.data || response;
    } catch (error) {
      console.error('[Billing] getStripeProducts error:', error);
      throw error;
    }
  },

  async createStripeCustomer(email) {
    const body = { email };
    console.log('[Billing] createStripeCustomer called with email:', email);
    try {
      // POST /stripe/customers no requiere autenticación (upsert user + customer)
      const response = await API.createStripeCustomer(body, {});
      console.log('[Billing] createStripeCustomer success:', response);
      const data = response.data || response;
      // Persist the customer token — used as the Bearer for one-time-payment.
      if (data?.token) {
        this.stripeCustomerToken = data.token;
        this.setApiAuth(`Bearer ${data.token}`);
      }
      return data;
    } catch (error) {
      console.error('[Billing] createStripeCustomer error:', error);
      throw error;
    }
  },

  async createStripeOneTimePayment(productId, paymentMethodId) {
    const body = {
      productId,
      paymentMethodId,
    };
    // Use the Bearer token from the /stripe/customers response.
    const headers = this.stripeCustomerToken
      ? { Authorization: `Bearer ${this.stripeCustomerToken}` }
      : this.getHeaders();
    console.log('[Billing] createStripeOneTimePayment called with:', body);
    try {
      const response = await API.createStripeOneTimePayment(body, headers);
      console.log('[Billing] createStripeOneTimePayment success:', response);
      return response.data || response;
    } catch (error) {
      console.error('[Billing] createStripeOneTimePayment error:', error);
      throw error;
    }
  },
};

export default Billing;
